import { publicTransport, RpcClient } from "./api.ts";
import { authState, initializeAuth } from "./auth.ts";
import { AuthPanel } from "./components/auth-panel.tsx";
import { Composer } from "./components/composer.tsx";
import { Navigation } from "./components/navigation.tsx";
import { SearchPanel } from "./components/search.tsx";
import { config } from "./config.ts";
import { loadActorFilters } from "./filters.ts";
import { refreshUnread } from "./notifications.ts";
import { trackOwnProfile } from "./own-profile.ts";
import { type Route, Router } from "./router.ts";
import { ActorListView } from "./views/actor-list.tsx";
import { HomeView } from "./views/home.tsx";
import { NotificationsView } from "./views/notifications.tsx";
import { PostListView } from "./views/post-list.tsx";
import { ProfileView } from "./views/profile.tsx";
import { SearchView } from "./views/search.tsx";
import { ThreadView } from "./views/thread.tsx";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
if (isIOS) {
  // iOS honors this for input auto-zoom but still permits pinch zoom.
  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (viewport) viewport.content += ", maximum-scale=1";
}

const client = new RpcClient(publicTransport(config.appView));
const content = <div class="route-content"></div>;
const router = new Router((route, signal) => {
  document.title = title(route);
  void refreshUnread(signal);
  switch (route.name) {
    case "home":
      content.replaceChildren(HomeView(signal));
      break;
    case "profile":
      content.replaceChildren(ProfileView(route.actor, signal));
      break;
    case "actorList":
      content.replaceChildren(ActorListView(client, route.actor, route.kind, signal));
      break;
    case "post":
      content.replaceChildren(ThreadView(route.actor, route.rkey, signal));
      break;
    case "postList":
      content.replaceChildren(PostListView(client, route.actor, route.rkey, route.kind, signal));
      break;
    case "notifications":
      content.replaceChildren(NotificationsView(signal));
      break;
    case "search":
      content.replaceChildren(SearchView(router));
      break;
    case "oauthCallback":
      content.replaceChildren(
        <main class="message-page">
          <p class="status">Finishing sign-in…</p>
        </main>,
      );
      break;
    case "notFound":
      content.replaceChildren(
        <main class="message-page">
          <h1>Page not found</h1>
          <p>
            <a href="/" dataset={{ route: "" }}>Return home</a>
          </p>
        </main>,
      );
      break;
  }
});
const shell = (
  <div class="app-shell">
    <header class="site-header">
      {Navigation()}
      {Composer(router)}
      {AuthPanel()}
    </header>
    <div class="center-column">
      {content}
    </div>
    <aside class="right-rail">
      {SearchPanel(router)}
      <footer class="site-footer">
        <span>cerulea alpha: deluxe microblogging on the AT Protocol</span>
      </footer>
    </aside>
  </div>
);

document.body.replaceChildren(shell);
authState.subscribeImmediate((state) => void loadActorFilters(state).catch(() => {}));
trackOwnProfile();

router.start();

void initializeAuth().then((returnTo) => {
  if (returnTo) router.navigate(returnTo, true);
  else if (location.pathname === new URL(config.oauthRedirectUri).pathname) {
    router.navigate("/", true);
  }
});

function title(route: Route): string {
  switch (route.name) {
    case "home":
      return "Cerulea";
    case "profile":
      return `${route.actor} - Cerulea`;
    case "actorList":
      return `${route.kind === "followers" ? "Followers" : "Following"} · ${route.actor} · Cerulea`;
    case "post":
      return "Post - Cerulea";
    case "postList":
      return `${route.kind[0]!.toUpperCase()}${route.kind.slice(1)} · Post · Cerulea`;
    case "notifications":
      return "Notifications · Cerulea";
    case "search":
      return "Search · Cerulea";
    case "oauthCallback":
      return "Signing in · Cerulea";
    case "notFound":
      return "Not found · Cerulea";
  }
}
